import {
  CustomProductOptionConfigItemType,
  CustomProductOptionConfigType,
  CustomProductOptionTitle,
  CustomProductOptionType,
  ICustomProductOptionConfig,
  ICustomProductOptionConfigItem,
} from "@evennt-tech/api-v2-core";
import { OrderProductCpoConfig } from "ts/api/order";
import { ProductConfig, ShowProductConfig } from "ts/api/product";

export const isColorItem = ({
  type,
  cpo,
}: Pick<ICustomProductOptionConfigItem, "cpo" | "type">): boolean =>
  !type &&
  (cpo
    ? cpo.type === CustomProductOptionType.ADMIN &&
      cpo.title === CustomProductOptionTitle.COLOR
    : true);

export const getMergedProductId = (
  config?: ICustomProductOptionConfig | null,
  productId?: string
): string => {
  if (
    config?.type !== CustomProductOptionConfigType.CUSTOM ||
    !config.items?.length
  ) {
    return productId ?? "";
  }
  const { defaultItems, customItems } = config.items.reduce<{
    customItems: string[];
    defaultItems: string[];
  }>(
    (acc, curr) => {
      if (!curr.cpoItem) {
        return acc;
      }

      if (isColorItem(curr)) {
        acc.defaultItems.push(curr.cpoItem.name);
      } else if (curr.cpoItem.optionId) {
        acc.customItems.push(curr.cpoItem.optionId);
      }
      return acc;
    },
    { customItems: [], defaultItems: [] }
  );

  return [
    ...(productId ? [productId] : []),
    ...defaultItems,
    ...customItems,
  ].join("-");
};

export const transformOrderProductCpoConfig = (
  config: ICustomProductOptionConfig
): OrderProductCpoConfig => {
  return {
    id: config.id,
    priceId: config.prices?.[0]?.id as string,
    isCustom: isCustomProductOptionConfigCustom(config),
    items: (config.items ?? []).map(
      ({ id, cpoConfigItemToFiles, cpo, cpoItem }) => {
        const itemToFiles = cpoConfigItemToFiles?.length
          ? cpoConfigItemToFiles?.map(({ id: fileId }) => fileId)
          : null;

        return {
          id,
          ...(!!itemToFiles?.length && { itemToFiles }),
          cpoTitle: cpo?.title ?? "",
          name: cpoItem?.name ?? "",
          ...(cpoItem?.optionId && { optionId: cpoItem.optionId }),
          isCustom: cpo?.type === CustomProductOptionType.SUPPLIER,
        };
      }
    ),
    ...(!!config.cpoConfigToFiles?.length && {
      configToFiles: config.cpoConfigToFiles.map(({ id }) => id),
    }),
  };
};

export const isCustomProductOptionConfigCustom = <
  T extends Pick<ICustomProductOptionConfig, "type">
>(
  config: T
): boolean => config.type === CustomProductOptionConfigType.CUSTOM;

export const isCpoConfigQuantityAvailable = (
  config: ProductConfig
): boolean => {
  const configQty = config.showQuantity ?? config.quantity ?? 0;
  const purchasedQty = config.purchasedQuantity ?? 0;

  return configQty - purchasedQty > 0;
};

export const getCpoConfigAvailableQty = (config?: ShowProductConfig) => {
  if (!config) {
    return 0;
  }
  const configQty = config.showQuantity ?? config.quantity;

  return configQty - (config.purchasedQuantity ?? 0);
};

export const isConfigItemImage = <T extends ICustomProductOptionConfigItem>(
  configItem: T
): boolean => configItem.type === CustomProductOptionConfigItemType.IMAGE;

export const getColorItemsName = (
  firstColorName: string,
  secondColorName?: string
) => `${firstColorName}${secondColorName ? `-${secondColorName}` : ""}`;
